<script>
    import Datepicker from 'vuejs-datepicker';
    import moment from 'moment';
    import spinner from "shared/Components/spinner.vue";
    import { createEvent } from "shared/eventBus";

    export default {
        props: {
            id: String,
            minDate: {
                type: Date | String,
                default: () => new Date(1900, 0, 1)
            },
            maxDate: {
                type: Date | String,
                default: () => new Date()
            },
            openDate: {
                type: Date,
                default: () => new Date(1980, 0, 1)
            },
            validationScope: String,
            validateOnChange: {
                type: Boolean,
                default: true
            },
            inline: Boolean,
            value: Date,
            name: String,
            format: {
                type: String,
                default: "M/D/YYYY"
            },
            disabled: Boolean,
            saved: Boolean,
            required: Boolean,
            disableDates: {
                type: Array,
                default: []
            }, 
            loading: Boolean
        },
        data() {
            return {
                selectedDate: null,
                myDatesBetween: null,
                forceSelect: false,
                changed: false,
                isRight: true//todo -- calculate if we're on the right side of the screen during 'opened' event and set to true -- for now having true always works decently (aligns datepicker to right edge of input preventing it from going off the screen)
        }
        },
        mounted() {
            this.selectedDate = this.value;
            this.myDatesBetween = this.getMyDatesBetween();

            let pickerInput = this.$refs.picker.$el.querySelector("input")

            pickerInput.addEventListener("change", () => this.$refs.picker.resetTypedDate = new Date());
            pickerInput.addEventListener("focus", e => e.target.select())
        },
        components: {
            Datepicker,
            spinner
        },
        computed: {
            disabledDates () {
                return {
                    to: this.actualMin,
                    from: this.actualMax,
                    dates: this.disabledDateObjects
                };
            },
            disabledDateObjects() {
                return this.disableDates.map(d => moment(d, this.format).toDate());
            },
            actualMin () {
                if(this.minDate instanceof Date) {
                    return this.minDate;
                }
                
                if (typeof this.minDate === "string") {
                    return moment(this.minDate, this.format).toDate();
                }

                return null;
            },
            actualMax() {
                if(this.maxDate instanceof Date) {
                    return this.maxDate;
                }
                
                if (typeof this.maxDate === "string") {
                    return moment(this.maxDate, this.format).toDate();
                }

                return null;
            },
            validation () {
                return {
                    required: !!this.required,
                    date_format: this.format,
                    exclude_dates: this.disabledDateObjects,
                    date_between: {
                        min: moment(this.minDate).format(this.format),
                        max: moment(this.maxDate).format(this.format)
                    }
                }
            },
            dateFormat() {
                return date => moment(date).format(this.format);
            }
        },
        watch: {
            value(val) {
                this.selectedDate = val;
            },
            saved(val) {
                if(val) {
                    this.changed = false;
                }
            }
        },
        methods: {
            setCell(d) {
                //This adds the date as an attribute to the cell, making selection easier for automation
                return `<span data-date='${moment(new Date(d.timestamp)).format("YYYY-MM-DD")}'>${d.date}</span>`;
            },
            onSelected (selectedDate) {
                this.$emit("selected", selectedDate);
                this.$emit("change", selectedDate);
                this.$emit("input", selectedDate);
                this.$emit("change", selectedDate);

                this.changed = true;
                
                this.$el.dispatchEvent(createEvent("change"));
                
                if(this.name && this.validateOnChange && this.$validator) {
                    this.$validator.validate(this.name, selectedDate, this.validationScope);
                }
            },
            getMyDatesBetween () {
                if(!this.name) {
                    return null;
                }

                var field = this.$parent.$validator.fields.items.filter(field => field.name === this.name)[0];
                if(!field || !field.rules.date_between || !field.rules.date_between[0]) {
                    return null;
                }

                return field.rules.date_between[0];
            },
            open() {
                if(!this.$refs.picker.isOpen) {
                    this.$refs.picker.$el.querySelector("input").focus();
                    this.$refs.picker.showCalendar();
                }
            },
            close() {
                if(this.$refs.picker.isOpen) {
                    this.$refs.picker.close(true);
                }
            },
            focus(open) {
                this.$refs.picker.$el.querySelector("input").focus();

                if(open) {
                    this.open();
                }
            }
        },
        inject: ["$validator"]
    }
</script>

<template>
    <div class="datepicker-parent" :disabled="disabled" @click.prevent="">
        <datepicker ref="picker" class="pointer f9datepicker" :class="{ right: isRight }" :id="id" v-model="selectedDate" :format="dateFormat" 
            :data-vv-name="name" v-validate="validation" :data-vv-scope="validationScope" data-vv-validate-on="" :day-cell-content="setCell"
            :disabled-dates="disabledDates" :open-date="openDate" :typeable="true" :disabled="disabled" :inline="inline" :name="name"
            @selected="onSelected" @opened="$emit('opened', $event)" @closed="$emit('closed', $event)" calendar-class="f9date" 
            @keyup.enter.native.stop.prevent="open">
        </datepicker>
        <img v-show="!loading" src="https://flyfrontier-com-frontdoor-en-chh3dufqdpdff8h9.a02.azurefd.net/images/date-picker-icon.png" alt=""/>
        <spinner v-if="loading" :visible="loading" half></spinner>
    </div>
</template>

<style lang="less" scoped>
    @import "~less-variables";
    @import "~less-mixins";

    .datepicker-parent {
        position: relative;

        img {
            position: absolute;
            width: 14px;
            height: 16px;
            right: 12px;
            top: 10px;
            cursor: pointer;
            pointer-events: none;
        }

        &[disabled] {
            background-color: @inputDisabledBackground;
            cursor: not-allowed;
            
            * {
                cursor: not-allowed !important;
            }

            img {
                display: none;
            }
        }

        @{deep} input {
            font-family: 'Montserrat', sans-serif;
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;
            -moz-border-radius: 2px;
            -webkit-border-radius: 2px;
            border-radius: 2px;
            height: 40px;
            border: 1px solid #d6d7d5;
            font-size: 16px;
            width: 100%;

            @media @IE11 {
                &::-ms-clear {
                    display: none;
                }

                padding: 0 !important;
                padding-left: 10px !important;
            }
        }

        .lds-ring {
            position:absolute !important;
            right: 0;
            top: 0;
            width: 100% !important;
            height: 100% !important;
            background-color: rgba(255,255,255,0.4);
            cursor: default;

            @{deep} div {
                right: 12px;
                left: auto;
            }
        }
    }

    .f9date {
        font-size: 16px;

        &.vdp-datepicker__calendar {
            header {
                .prev::after {
                    border-right-color: @frontier-green;
                }
                .next::after {
                    border-left-color: @frontier-green;
                }

                .up:hover {
                    text-decoration: underline;
                }
            }

            .cell {
                &:not(.blank):not(.disabled).day:hover, 
                &:not(.blank):not(.disabled).month:hover, 
                &:not(.blank):not(.disabled).year:hover {
                    border: 1px solid @frontier-green;
                }

                &.selected {
                    background: @frontier-green;
                    color: @frontier-white;
                }
            }
        }

        @media @mobile {
            .right {
                right: 0;
            }
        }
    }

    body.user-is-tabbing {
        .datepicker-parent:focus-within {
            border: 4px solid #aaa;
        }
    }
</style>